import { Address } from '@liveauctioneers/caterwaul-components/lib/Address/Address';
import { BidderAddress } from '@/types/BidderAddresses';
import { BlueLinkSecondaryUnderlined, LabelMini } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getFormattedPhoneNumber,
    getNationalPhoneNumberForm,
} from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/utils/phoneForm';
import { getUpdateAccountLoading } from '@/redux/modules/account/updateAccount/updateAccount';
import { isUserDataLoading } from '@/redux/modules/account/user/user.selectors';
import { openAddressModal } from '@/redux/modules/modal';
import { updateBidderAddress } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import styled from 'styled-components';
type Props = {
    address: BidderAddress;
    canEdit: boolean;
    onSelect: (address: BidderAddress) => void;
    selected: boolean;
    selectedId: number;
    showRemoveButton: boolean;
    showSetDefaultButton?: boolean;
};

const BidderAddressListItem = ({
    address,
    canEdit,
    onSelect,
    selected,
    selectedId,
    showRemoveButton,
    showSetDefaultButton,
}: Props) => {
    const dispatch = useAppDispatch();

    const updateAccountLoading = useAppSelector(getUpdateAccountLoading);
    const userDataLoading = useAppSelector(isUserDataLoading);

    const openEditModal = (address: BidderAddress, id: number) => {
        dispatch(openAddressModal({ address, addressID: id }));
    };
    const removeAddress = (address: BidderAddress) => {
        dispatch(updateBidderAddress(address.bidderId, { ...address, enabled: false }, ''));
    };

    const formattedPhoneNumber = getNationalPhoneNumberForm(address.phoneNumber);
    const phoneNumber =
        formattedPhoneNumber.countryCode === 'US' || formattedPhoneNumber.countryCode === 'CA'
            ? getFormattedPhoneNumber(address.phoneNumber)
            : formattedPhoneNumber.phoneNumber;

    const addressComponent = (
        <AddressRow>
            <AddressCol>
                {address.isDefault && (
                    <StyledDefaultLabel>
                        <FormattedMessage id="default" />
                    </StyledDefaultLabel>
                )}
                <StyledAddress address={address} />
            </AddressCol>
        </AddressRow>
    );

    return (
        <StyledBidderAddessListItem data-testid="addressListItem">
            {showSetDefaultButton ? (
                <AddressRow>
                    <AddressCol>
                        {address.isDefault && (
                            <StyledDefaultLabel>
                                <FormattedMessage id="default" />
                            </StyledDefaultLabel>
                        )}
                        <StyledAddress address={{ ...address, phoneNumber }} />
                    </AddressCol>
                </AddressRow>
            ) : (
                <StyledRadio
                    $selected={selected}
                    disabled={updateAccountLoading || userDataLoading}
                    id={address.addressId}
                    label={addressComponent}
                    onClick={() => {
                        if (!selected) {
                            onSelect(address);
                        }
                    }}
                    selectedName={selectedId.toString()}
                />
            )}
            {canEdit ? (
                <StyledEditRow $radioIndentation={!showSetDefaultButton}>
                    <BlueLinkSecondaryUnderlined onClick={() => openEditModal(address, address.addressId)}>
                        <FormattedMessage id="addresses.edit" />
                    </BlueLinkSecondaryUnderlined>
                    {showRemoveButton && !address.isDefault && (
                        <StyledLink onClick={() => removeAddress(address)}>
                            <FormattedMessage id="addresses.remove" />
                        </StyledLink>
                    )}
                    {!address.isDefault && showSetDefaultButton && (
                        <StyledLink onClick={() => onSelect(address)}>
                            <FormattedMessage id="addresses.setDefault" />
                        </StyledLink>
                    )}
                </StyledEditRow>
            ) : (
                <span></span>
            )}
        </StyledBidderAddessListItem>
    );
};

export default BidderAddressListItem;

const StyledBidderAddessListItem = styled.div`
    margin-bottom: 24px;
`;

const StyledDefaultLabel = styled(LabelMini)`
    margin-bottom: 4px;
    display: inline-block;
`;

const StyledEditRow = styled.div<{ $radioIndentation: boolean }>`
    margin-top: 4px;
    margin-left: ${({ $radioIndentation }) => ($radioIndentation ? 32 : 0)}px;
`;

const AddressRow = styled.div`
    display: flex;
`;

const AddressCol = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledAddress = styled(Address)`
    margin-bottom: 0;
`;

const StyledLink = styled(BlueLinkSecondaryUnderlined)`
    margin-left: 16px;
`;

const StyledRadio = styled(Radio).attrs(() => ({
    radioCircleClassName: 'radioCircleClassName',
}))<{ $selected?: boolean }>`
    padding-left: 26px;

    .radioCircleClassName {
        top: 3px;
        left: 0;
    }
`;
